import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  useGetUserReviewsGivenQuery,
  useGetUserReviewsReceivedQuery,
  useDeleteReviewMutation
} from '../../redux/apis/endpoints/reviewApi';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import Button from '../../components/ui/Button';
import Rating from '../../components/ui/Rating';
import { PencilIcon, TrashIcon, CubeIcon, InboxArrowDownIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import Modal from '../../components/ui/Modal';
import BackButton from '../../components/common/BackButton';
import { debounce } from 'lodash';

const formatDate = (dateString) => {
  if (!dateString) return 'Unknown date';
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric'
  });
};

const TabButton = ({ children, onClick, isActive }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium rounded-md focus:outline-none transition-colors duration-150 ease-in-out 
      ${isActive 
        ? 'bg-brand-teal text-white' 
        : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800'
      }`}
  >
    {children}
  </button>
);

const UserReviewsPage = () => {
  const history = useHistory();
  const { userId: routeUserId } = useParams();
  const loggedInUser = useSelector(state => state.auth.user);
  const scrollContainerRef = useRef(null);
  const isOwnReviews = loggedInUser?.id === parseInt(routeUserId, 10);

  const [activeTab, setActiveTab] = useState('received'); // 'received' or 'given'

  // State for Reviews Received
  const [pageReceived, setPageReceived] = useState(1);
  const [allReviewsReceived, setAllReviewsReceived] = useState([]);
  const [hasMoreReceived, setHasMoreReceived] = useState(true);
  const pageSize = 10;

  // State for Reviews Given
  const [pageGiven, setPageGiven] = useState(1);
  const [allReviewsGiven, setAllReviewsGiven] = useState([]);
  const [hasMoreGiven, setHasMoreGiven] = useState(true);

  // Effect to manage activeTab based on isOwnReviews
  useEffect(() => {
    if (!isOwnReviews && activeTab === 'given') {
      setActiveTab('received');
    }
    // When routeUserId changes, ensure data for both tabs is reset
    // and pagination is reset to 1 for both.
    setAllReviewsReceived([]);
    setPageReceived(1);
    setHasMoreReceived(true);
    setAllReviewsGiven([]);
    setPageGiven(1);
    setHasMoreGiven(true);

    // If not own reviews, set activeTab to received to ensure correct initial data load for received tab.
    // If it is own reviews, activeTab could have been 'given' from a previous state, so let it be.
    // The initial useState for activeTab is 'received', covering the first load of any profile.
    if (!isOwnReviews) {
        setActiveTab('received');
    }

  }, [routeUserId, isOwnReviews]); // isOwnReviews is derived from routeUserId

  const {
    data: reviewsReceivedData,
    isLoading: isLoadingReceived,
    isFetching: isFetchingReceived,
    isError: isErrorReceived,
    error: errorReceived
  } = useGetUserReviewsReceivedQuery(
    { userId: routeUserId, page: pageReceived, pageSize },
    { skip: !routeUserId || activeTab !== 'received' } 
  );

  const {
    data: reviewsGivenData,
    isLoading: isLoadingGiven,
    isFetching: isFetchingGiven,
    isError: isErrorGiven,
    error: errorGiven
  } = useGetUserReviewsGivenQuery(
    { userId: routeUserId, page: pageGiven, pageSize },
    { skip: !routeUserId || !isOwnReviews || activeTab !== 'given' } // Also skip if not own reviews page
  );

  const [deleteReview, { isLoading: isDeletingReview }] = useDeleteReviewMutation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState(null);

  // Effect for RECEIVED reviews data
  useEffect(() => {
    if (reviewsReceivedData && activeTab === 'received') {
      if (pageReceived === 1) {
        setAllReviewsReceived(reviewsReceivedData);
      } else {
        setAllReviewsReceived(prevReviews => {
          const existingIds = new Set(prevReviews.map(r => r.id));
          const uniqueNewReviews = reviewsReceivedData.filter(r => !existingIds.has(r.id));
          return [...prevReviews, ...uniqueNewReviews];
        });
      }
      setHasMoreReceived(reviewsReceivedData.length === pageSize);
    }
  }, [reviewsReceivedData, pageSize, pageReceived, activeTab]);

  // Effect for GIVEN reviews data
  useEffect(() => {
    if (reviewsGivenData && activeTab === 'given' && isOwnReviews) {
      if (pageGiven === 1) {
        setAllReviewsGiven(reviewsGivenData);
      } else {
        setAllReviewsGiven(prevReviews => {
          const existingIds = new Set(prevReviews.map(r => r.id));
          const uniqueNewReviews = reviewsGivenData.filter(r => !existingIds.has(r.id));
          return [...prevReviews, ...uniqueNewReviews];
        });
      }
      setHasMoreGiven(reviewsGivenData.length === pageSize);
    }
  }, [reviewsGivenData, pageSize, pageGiven, activeTab, isOwnReviews]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      const isAtBottom = scrollHeight - scrollTop <= clientHeight + 200; // Buffer

      if (isAtBottom) {
        if (activeTab === 'received' && hasMoreReceived && !isFetchingReceived) {
          setPageReceived(prevPage => prevPage + 1);
        }
        if (isOwnReviews && activeTab === 'given' && hasMoreGiven && !isFetchingGiven) {
          setPageGiven(prevPage => prevPage + 1);
        }
      }
    };

    const debouncedScrollHandler = debounce(handleScroll, 200);
    window.addEventListener('scroll', debouncedScrollHandler);
    
    return () => {
      window.removeEventListener('scroll', debouncedScrollHandler);
      debouncedScrollHandler.cancel();
    };
  }, [activeTab, hasMoreReceived, isFetchingReceived, hasMoreGiven, isFetchingGiven, setPageReceived, setPageGiven, isOwnReviews]);

  const handleTabChange = (tab) => {
    if (tab === 'given' && !isOwnReviews) return; // Prevent switching to given if not allowed
    setActiveTab(tab);
    // Reset pages to 1 for the new tab to load fresh data
    // Data arrays are cleared by the main useEffect on routeUserId or by data fetching for page 1
    if (tab === 'received') {
        setPageReceived(1);
        setAllReviewsReceived([]); 
        setHasMoreReceived(true);
    } else if (tab === 'given') {
        setPageGiven(1);
        setAllReviewsGiven([]); 
        setHasMoreGiven(true);
    }
  };

  const handleEditReview = (review) => {
    const fulfillmentGroupId = review.fulfillmentGroup?.id;

    if (review.isReviewCustomer) {
      const customerBeingReviewedId = review.customers?.[0]?.id;
      const orderId = review.orders?.[0]?.id;

      if (!customerBeingReviewedId) {
        toast.error('Cannot edit review: Customer being reviewed is missing.');
        return;
      }
      if (!fulfillmentGroupId) {
        toast.error('Cannot edit review: Fulfillment group context is missing for this customer review.');
        return;
      }
      history.push(`/customer/${customerBeingReviewedId}/fg/${fulfillmentGroupId}/review`, {
        existingReview: review,
        orderId: orderId,
      });
    } else {
      const productId = review.products?.[0]?.id;
      if (!productId) {
        toast.error('Cannot edit review: Product information is missing.');
        return;
      }
      const path = fulfillmentGroupId
        ? `/fulfillmentgroup/${fulfillmentGroupId}/product/${productId}/review` 
        : `/product/${productId}/review`;
      history.push(path, {
        existingReview: review,
        orderId: review.orders?.[0]?.id,
      });
    }
  };

  const openDeleteConfirm = (review) => {
    setReviewToDelete(review); // review is the full review object
    setShowDeleteModal(true);
  };

  const handleDeleteReview = async () => {
    if (!reviewToDelete) return;
    try {
      await deleteReview({
        id: reviewToDelete.id,
        originalReviewData: reviewToDelete 
      }).unwrap();
      toast.success('Review deleted successfully!');
      setShowDeleteModal(false);
      
      if (activeTab === 'received') {
        setAllReviewsReceived(prev => prev.filter(r => r.id !== reviewToDelete.id));
      } else if (activeTab === 'given' && isOwnReviews) {
        setAllReviewsGiven(prev => prev.filter(r => r.id !== reviewToDelete.id));
      }
      setReviewToDelete(null);
    } catch (err) {
      toast.error(err.data?.message || err.message || 'Failed to delete review.');
      setShowDeleteModal(false);
    }
  };

  const isLoadingCurrentTab = (activeTab === 'received' && isLoadingReceived && pageReceived === 1) || 
                            (isOwnReviews && activeTab === 'given' && isLoadingGiven && pageGiven === 1);
  const isErrorCurrentTab = (activeTab === 'received' && isErrorReceived) || 
                          (isOwnReviews && activeTab === 'given' && isErrorGiven);
  const errorCurrentTab = activeTab === 'received' ? errorReceived : (isOwnReviews && activeTab === 'given' ? errorGiven : null);

  const currentReviews = activeTab === 'received' ? allReviewsReceived : (isOwnReviews ? allReviewsGiven : []);
  const currentIsFetching = activeTab === 'received' ? isFetchingReceived : (isOwnReviews ? isFetchingGiven : false);
  const currentHasMore = activeTab === 'received' ? hasMoreReceived : (isOwnReviews ? hasMoreGiven : false);
  const currentPage = activeTab === 'received' ? pageReceived : (isOwnReviews ? pageGiven : 1);

  const pageTitle = isOwnReviews ? "My Reviews" : "User Reviews";
  
  const noReviewsMessage = activeTab === 'received' 
    ? (isOwnReviews ? "You haven't received any reviews yet." : "This user hasn't received any reviews yet.")
    : (isOwnReviews ? "You haven't written any reviews yet. Start sharing your thoughts!" : "This user hasn't written any reviews yet."); // This will only be shown if isOwnReviews is true and tab is given

  const renderReviewList = (reviews) => {
    if (reviews.length === 0 && !currentIsFetching && !isLoadingCurrentTab) {
  return (
        <div className="text-center py-12 bg-white rounded-lg shadow mt-6">
          {(activeTab === 'received' || !isOwnReviews) ? <InboxArrowDownIcon className="mx-auto h-12 w-12 text-gray-400" /> : <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-gray-400" />}
          <h3 className="mt-2 text-lg font-medium text-gray-900">
            {(activeTab === 'received' || !isOwnReviews) ? 'No Reviews Received' : 'No Reviews Given'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">{noReviewsMessage}</p>
        </div>
      );
    }

    return (
      <div className="space-y-6 mt-6">
        {reviews.map((review) => {
            const product = review.products?.[0];
          let isAuthorOfThisReview = false;
          if (review.isReviewCustomer) { 
            isAuthorOfThisReview = loggedInUser?.id === review.merchants?.[0]?.id;
          } else { 
            isAuthorOfThisReview = loggedInUser?.id === review.customers?.[0]?.id;
          }

            return (
              <div key={review.id} className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
                {product && (
                  <div className="mb-3 pb-3 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                      {product.photos?.[0]?.formats?.thumbnail?.url || product.photos?.[0]?.url ? (
                        <img 
                          src={product.photos[0].formats.thumbnail.url || product.photos[0].url}
                          alt={product.name || 'Product image'}
                          className="w-16 h-16 rounded-md object-cover border border-gray-200"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-md bg-gray-100 flex items-center justify-center text-gray-400">
                          <CubeIcon className="h-8 w-8" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-gray-500">Review for:</p>
                        <p 
                          className="font-semibold text-gray-800 hover:text-brand-teal cursor-pointer"
                          onClick={() => history.push(`/product/${product.id}`)}
                        >
                          {product.name || 'Unnamed Product'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-start justify-between">
                  <div className="flex-grow">
                    <div className="mb-2">
                      <Rating rating={review.rating} showCount={false} size="sm" />
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-3 whitespace-pre-wrap">
                      {review.comment}
                    </p>
                    <p className="text-xs text-gray-500 mb-3">
                      Reviewed on: {formatDate(review.createdAt || review.created_at)}
                    </p>
                    {review.pictures && review.pictures.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {review.pictures.map(pic => (
                          <img 
                            key={pic.id} 
                            src={pic.url} 
                            alt={`Review image ${pic.id}`}
                            className="w-16 h-16 sm:w-20 sm:h-20 rounded-md object-cover border border-gray-200"
                          />
                        ))}
                      </div>
                    )}
                  </div>

                {isAuthorOfThisReview && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 ml-4 flex-shrink-0">
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditReview(review)}
                        className="flex items-center justify-center"
                      >
                        <PencilIcon className="h-4 w-4 mr-1.5" /> Edit
                      </Button>
                      <Button 
                        variant="secondary"
                        size="sm"
                        onClick={() => openDeleteConfirm(review)}
                        className="flex items-center justify-center"
                        disabled={isDeletingReview && reviewToDelete?.id === review.id}
                      >
                        <TrashIcon className="h-4 w-4 mr-1.5" /> Delete
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
    );
  };

  if (isLoadingCurrentTab && currentPage === 1) return <div className="p-6"><LoadingSpinner /></div>;
  if (isErrorCurrentTab && !(activeTab ==='given' && !isOwnReviews) ) return <div className="p-6"><ErrorMessage message={errorCurrentTab?.data?.message || 'Could not load reviews.'} /></div>;

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen" ref={scrollContainerRef}>
      <div className="mb-4">
        <BackButton />
      </div>
      <h1 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-6">{pageTitle}</h1>

      <div className="mb-6 flex space-x-2 border-b border-gray-200 pb-2">
        <TabButton onClick={() => handleTabChange('received')} isActive={activeTab === 'received'}>
          Reviews Received
        </TabButton>
        {isOwnReviews && (
          <TabButton onClick={() => handleTabChange('given')} isActive={activeTab === 'given'}>
            Reviews Given
          </TabButton>
        )}
      </div>

      {renderReviewList(currentReviews)}

      {currentIsFetching && currentPage > 1 && (
        <div className="text-center py-6">
          <LoadingSpinner />
        </div>
      )}

      {!currentIsFetching && !currentHasMore && currentReviews.length > 0 && (
        <p className="text-center text-gray-500 py-6">You've reached the end of the reviews for this section.</p>
      )}

      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirm Delete Review"
      >
        <p className="text-gray-600 mb-6">Are you sure you want to delete this review? This action cannot be undone.</p>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={isDeletingReview}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteReview} isLoading={isDeletingReview}>
            Delete Review
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default UserReviewsPage; 